import React, { type ReactElement } from 'react'
import Card from '../../shared/UIv2/Card/Card'
import FileInput from '../../shared/UIv2/Input/FileInput'
import Icon from '../../../app/icon/Icon'
import { getCountries } from '../../../functions/getDataForSelects/getCountries'

type DocumentKind = 'passport' | 'emirates_id'

interface IdentityDocument {
    kind: DocumentKind
    title: string
    enabled: boolean
    expanded: boolean
    status: 'Not added' | 'Upload required' | 'Needs completion' | 'Ready'
    files: Array<{ label: string, file?: File, link?: string }>
    fields: Array<{ key: string, label: string, value?: string, type?: 'text' | 'date' | 'select' }>
    useSingleFile?: boolean
}

interface OwnerIdentityDocumentsProps {
    summary: string
    conflictKeys: Array<'DateOfBirth' | 'Country' | 'Gender'>
    conflictValues: Partial<Record<'DateOfBirth' | 'Country' | 'Gender', string | undefined>>
    documents: IdentityDocument[]
    onAdd: (kind: DocumentKind) => void
    onToggle: (kind: DocumentKind) => void
    onFile: (kind: DocumentKind, side: 'single' | 'front' | 'back', files: FileList | null) => void
    onFieldChange: (kind: DocumentKind, key: string, value: string) => void
    onFieldCommit: () => void
    onConflictResolve: (key: 'DateOfBirth' | 'Country' | 'Gender', value: string) => void
    onUseSingleFile: (enabled: boolean) => void
}

const statusClass: Record<IdentityDocument['status'], string> = {
    'Not added': 'badge badge-light',
    'Upload required': 'badge badge-light-warning',
    'Needs completion': 'badge badge-light-warning',
    Ready: 'badge badge-light-success'
}

const fileName = (link?: string): string | undefined => {
    if (link == null || link === '') return undefined
    return link.split('?')[0].split('/').pop() || undefined
}

function DocumentCard ({ document, onAdd, onToggle, onFile, onFieldChange, onFieldCommit, onUseSingleFile }: {
    document: IdentityDocument
    onAdd: OwnerIdentityDocumentsProps['onAdd']
    onToggle: OwnerIdentityDocumentsProps['onToggle']
    onFile: OwnerIdentityDocumentsProps['onFile']
    onFieldChange: OwnerIdentityDocumentsProps['onFieldChange']
    onFieldCommit: OwnerIdentityDocumentsProps['onFieldCommit']
    onUseSingleFile: OwnerIdentityDocumentsProps['onUseSingleFile']
}): ReactElement {
    return <Card rounded="8" style="white">
        <div className="d-flex align-items-center justify-content-between p-4 border-bottom">
            <div>
                <div className="fw-bold text-dark">{document.title}</div>
                <span className={statusClass[document.status]}>{document.status}</span>
            </div>
            {document.enabled
                ? <button type="button" className="btn btn-sm btn-light" onClick={() => onToggle(document.kind)}>{document.expanded ? 'Hide details' : 'View / edit'}</button>
                : <button type="button" className="btn btn-sm btn-primary" onClick={() => onAdd(document.kind)}>Add {document.title}</button>}
        </div>
        {document.enabled && <div className="p-4">
            {document.kind === 'emirates_id' && <label className="form-check form-switch form-check-custom form-check-solid mb-4">
                <input className="form-check-input" type="checkbox" checked={document.useSingleFile === true} onChange={(event) => onUseSingleFile(event.target.checked)} />
                <span className="form-check-label text-gray-700">Use the front file for both sides</span>
            </label>}
            {document.files.filter((_, index) => document.kind !== 'emirates_id' || document.useSingleFile !== true || index === 0).map((entry, index) => {
                const side = document.kind === 'passport' ? 'single' : index === 0 ? 'front' : 'back'
                return <div className="mb-4" key={entry.label}>
                    <div className="form-label fw-bold text-dark text-uppercase">{entry.label} <span className="text-danger">*</span></div>
                    <div className="d-flex align-items-center gap-3">
                        <div className="flex-grow-1"><FileInput placeholder={fileName(entry.link) ?? `Upload ${entry.label}`} multiple={false} accept="image/png, image/gif, image/jpeg, application/pdf" value={entry.file == null ? null : [entry.file]} onChange={(files) => onFile(document.kind, side, files)} /></div>
                        {entry.link != null && <button type="button" className="btn btn-icon btn-light" aria-label={`Open ${entry.label}`} onClick={() => window.open(entry.link)}><Icon name="download_for_offline_24dp" className="text-muted" /></button>}
                    </div>
                </div>
            })}
            {document.expanded && <div className="row g-3">
                {document.fields.map((field) => <div className="col-12" key={field.key}>
                    <label className="form-label fw-bold text-dark text-uppercase">{field.label} <span className="text-danger">*</span></label>
                    {field.key === 'nationality'
                        ? <select className="form-select form-select-solid" defaultValue={field.value ?? ''} onChange={(event) => onFieldChange(document.kind, field.key, event.target.value)} onBlur={onFieldCommit}><option value="">Select nationality</option>{getCountries().map((country) => <option value={country} key={country}>{country}</option>)}</select>
                        : field.type === 'select'
                        ? <select className="form-select form-select-solid" defaultValue={field.value ?? ''} onChange={(event) => onFieldChange(document.kind, field.key, event.target.value)} onBlur={onFieldCommit}><option value="">Select option</option><option value="Male">Male</option><option value="Female">Female</option></select>
                        : <input type={field.type ?? 'text'} className="form-control form-control-solid" defaultValue={field.value ?? ''} onChange={(event) => onFieldChange(document.kind, field.key, event.target.value)} onBlur={onFieldCommit} />}
                </div>)}
            </div>}
        </div>}
    </Card>
}

export default function OwnerIdentityDocuments ({ summary, conflictKeys, conflictValues, documents, onAdd, onToggle, onFile, onFieldChange, onFieldCommit, onConflictResolve, onUseSingleFile }: OwnerIdentityDocumentsProps): ReactElement {
    const unresolvedConflict = conflictKeys.some((key) => String(conflictValues[key] ?? '').trim() === '')
    return <div className="fv-row py-3 w-100">
        <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="fw-bold text-dark text-uppercase">Identity documents</div>
            <span className={conflictKeys.length === 0 ? 'text-success fw-semibold' : unresolvedConflict ? 'text-warning fw-semibold' : 'text-success fw-semibold'}>{conflictKeys.length === 0 ? summary : unresolvedConflict ? 'Document data conflict — resolve below' : 'Document data conflict resolved manually'}</span>
        </div>
        <div className="d-flex flex-column gap-4">{documents.map((document) => <DocumentCard key={document.kind} document={document} onAdd={onAdd} onToggle={onToggle} onFile={onFile} onFieldChange={onFieldChange} onFieldCommit={onFieldCommit} onUseSingleFile={onUseSingleFile} />)}</div>
        {conflictKeys.length > 0 && <Card rounded="8" style="filled" customBackgroundClass="bg-light-warning mt-4"><div className="p-4">
            <div className="fw-bold text-dark mb-3">Choose the owner identity values</div>
            {conflictKeys.map((key) => <div className="mb-3" key={key}>
                <label className="form-label fw-bold text-dark text-uppercase">Owner {key === 'DateOfBirth' ? 'date of birth' : key === 'Country' ? 'country' : 'gender'}</label>
                {key === 'Gender'
                    ? <select className="form-select form-select-solid" value={conflictValues[key] ?? ''} onChange={(event) => onConflictResolve(key, event.target.value)}><option value="">Select option</option><option value="Male">Male</option><option value="Female">Female</option></select>
                    : <input type={key === 'DateOfBirth' ? 'date' : 'text'} className="form-control form-control-solid" value={conflictValues[key] ?? ''} onChange={(event) => onConflictResolve(key, event.target.value)} />}
            </div>)}
        </div></Card>}
    </div>
}
